import React, { useState, useMemo } from "react";
import { ZoneBreakdownModal } from "./zoneBreakdownModal";
import { AverageBreakdownModal } from "./averageBreakdownModal";

const getUtilizationColor = (percentage) => {
  if (percentage === null || percentage === 0) return "rgb(230, 230, 230)";
  if (percentage <= 33) return "hsl(120, 100%, 40%)";
  if (percentage <= 66) return "hsl(60, 100%, 50%)";
  if (percentage <= 95) return "hsl(30, 100%, 50%)";
  return "hsl(0, 100%, 40%)";
};

const getTextColor = (percentage) => {
  if (percentage === null || percentage === 0) return "black";
  if (percentage <= 33) return "white";
  if (percentage <= 66) return "black";
  return "white";
};

const HeatmapLegend = () => (
  <div className="d-flex align-items-center gap-2">
    <div className="d-flex align-items-center">
      <div style={{ height: "10px", width: "10px", backgroundColor: "rgb(230, 230, 230)", borderRadius: "2px" }} />
      <small className="ms-1">0%</small>
    </div>
    <div className="d-flex align-items-center">
      <div style={{ height: "10px", width: "10px", backgroundColor: "hsl(120, 100%, 40%)", borderRadius: "2px" }} />
      <small className="ms-1">0-33%</small>
    </div>
    <div className="d-flex align-items-center">
      <div style={{ height: "10px", width: "10px", backgroundColor: "hsl(60, 100%, 50%)", borderRadius: "2px" }} />
      <small className="ms-1">34-66%</small>
    </div>
    <div className="d-flex align-items-center">
      <div style={{ height: "10px", width: "10px", backgroundColor: "hsl(30, 100%, 50%)", borderRadius: "2px" }} />
      <small className="ms-1">67-95%</small>
    </div>
    <div className="d-flex align-items-center">
      <div style={{ height: "10px", width: "10px", backgroundColor: "hsl(0, 100%, 40%)", borderRadius: "2px" }} />
      <small className="ms-1">96-100%</small>
    </div>
  </div>
);

export const MonthlyHeatmap = ({ monthlyData, averageData = [], floorCapacity, loading }) => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showZoneBreakdown, setShowZoneBreakdown] = useState(false);
  const [selectedAverage, setSelectedAverage] = useState(null);
  const [showAverageBreakdown, setShowAverageBreakdown] = useState(false);

  const days = useMemo(() => {
    if (!monthlyData || monthlyData.length === 0) return [];
    return [...new Set(monthlyData.map((item) => item.date))].sort();
  }, [monthlyData]);

  // If averageData is provided from API, use it; otherwise calculate it
  const computedAverageData = useMemo(() => {
    if (averageData && averageData.length > 0) {
      console.log('monthlyHeatmap averageData from API:', averageData);
      return averageData;
    }

    // Fallback: Calculate average data if API doesn't provide it
    if (!monthlyData || monthlyData.length === 0) return [];

    const hourlyAggregation = {};

    monthlyData.forEach((day) => {
      if (day.hourlyData && day.hourlyData.length > 0) {
        day.hourlyData.forEach((entry) => {
          const hour = entry.hour;
          if (!hourlyAggregation[hour]) {
            hourlyAggregation[hour] = {
              totalCount: 0,
              totalPercentage: 0,
              days: 0,
              zoneData: {}
            };
          }
          hourlyAggregation[hour].totalCount += entry.count || 0;
          hourlyAggregation[hour].totalPercentage += entry.percentage || 0;
          hourlyAggregation[hour].days += 1;

          // Aggregate zone data
          if (entry.zones && Array.isArray(entry.zones)) {
            entry.zones.forEach((zone) => {
              if (!hourlyAggregation[hour].zoneData[zone.zoneName]) {
                hourlyAggregation[hour].zoneData[zone.zoneName] = {
                  totalCount: 0,
                  totalPercentage: 0,
                  days: 0,
                  capacity: zone.capacity
                };
              }
              hourlyAggregation[hour].zoneData[zone.zoneName].totalCount += zone.count || 0;
              hourlyAggregation[hour].zoneData[zone.zoneName].totalPercentage += zone.percentage || 0;
              hourlyAggregation[hour].zoneData[zone.zoneName].days += 1;
            });
          }
        });
      }
    });

    return Object.keys(hourlyAggregation).map((hour) => {
      const agg = hourlyAggregation[hour];

      // Calculate zone averages
      const zones = Object.keys(agg.zoneData).map((zoneName) => ({
        zoneName: zoneName,
        averageCount: Math.round(agg.zoneData[zoneName].totalCount / agg.zoneData[zoneName].days),
        averagePercentage: agg.zoneData[zoneName].totalPercentage / agg.zoneData[zoneName].days,
        capacity: agg.zoneData[zoneName].capacity
      }));

      return {
        hour: parseInt(hour),
        averageCount: Math.round(agg.totalCount / agg.days),
        averagePercentage: agg.totalPercentage / agg.days,
        zones
      };
    }).sort((a, b) => a.hour - b.hour);
  }, [monthlyData, averageData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getHourlyData = (date, hour) => {
    const dayData = monthlyData.find((item) => item.date === date);
    if (!dayData) return null;
    return dayData.hourlyData?.find((item) => item.hour === hour);
  };

  const getAverageData = (hour) => {
    return computedAverageData.find((item) => item.hour === hour) || null;
  };

  const handleCellClick = (entry) => {
    if (entry && entry.count > 0) {
      setSelectedEntry(entry);
      setShowZoneBreakdown(true);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!monthlyData || monthlyData.length === 0) {
    return <div className="alert alert-warning">No monthly data available.</div>;
  }

  return (
    <div>
      {/* Monthly Detailed Utilization */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Monthly Detailed Utilization</h3>
          <HeatmapLegend />
        </div>

        <div style={{ overflowX: "auto", paddingBottom: "8px" }}>
          {days.map((date, dayIndex) => (
            <div key={date} className="d-flex align-items-center mb-2" style={{ minWidth: "fit-content" }}>
              <div style={{ minWidth: "100px" }}>
                <small className="fw-medium">{formatDate(date)}</small>
              </div>

              {Array.from({ length: 24 }, (_, hour) => {
                const entry = getHourlyData(date, hour);

                if (!entry || entry.count === null || entry.count === 0 || entry.count === undefined) {
                  return (
                    <div key={hour} className="d-flex flex-column align-items-center" style={{ minWidth: "40px" }}>
                      {dayIndex === 0 && (
                        <div className="text-center mb-2">
                          <small style={{ fontSize: "10px" }}>
                            {`${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`}
                          </small>
                        </div>
                      )}
                      <div
                        style={{
                          height: "35px",
                          width: "35px",
                          borderRadius: "4px",
                          backgroundColor: "rgb(230, 230, 230)",
                          border: "1px solid #dee2e6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginLeft: "4px",
                          marginRight: "4px",
                        }}
                      >
                        <span style={{ fontSize: "8px", fontWeight: "bold", color: "#6c757d" }}>0%</span>
                      </div>
                    </div>
                  );
                }

                const percentageValue = entry.percentage || 0;
                const percentageDisplay = percentageValue.toFixed(1);
                const color = getUtilizationColor(percentageValue);
                const textColor = getTextColor(percentageValue);
                const isOverCapacity = entry.count > floorCapacity;

                return (
                  <div
                    key={hour}
                    className="d-flex flex-column align-items-center"
                    style={{ minWidth: "40px", cursor: "pointer" }}
                    onClick={() => handleCellClick(entry)}
                    title={`${hour}:00 - ${entry.count} people (${percentageDisplay}%)`}
                  >
                    {dayIndex === 0 && (
                      <div className="text-center mb-2">
                        <small style={{ fontSize: "10px" }}>
                          {`${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`}
                        </small>
                      </div>
                    )}
                    <div
                      style={{
                        height: "35px",
                        width: "35px",
                        borderRadius: "4px",
                        backgroundColor: color,
                        border: isOverCapacity ? "2px solid #dc3545" : "1px solid #dee2e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "4px",
                        marginRight: "4px",
                      }}
                    >
                      <span style={{ fontSize: "8px", fontWeight: "bold", color: textColor }}>
                        {percentageDisplay}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Average Utilization */}
      <div>
        <h3 className="mb-3">Monthly Average Utilization</h3>

        <div style={{ overflowX: "auto", paddingBottom: "8px" }}>
          <div className="d-flex align-items-center" style={{ minWidth: "fit-content" }}>
            <div style={{ minWidth: "100px" }}>
              <small className="fw-medium">Average</small>
            </div>

            {Array.from({ length: 24 }, (_, hour) => {
              const avgData = getAverageData(hour);

              if (!avgData || avgData.averageCount === null || avgData.averageCount === 0) {
                return (
                  <div key={hour} className="d-flex flex-column align-items-center" style={{ minWidth: "40px" }}>
                    <div className="text-center mb-2">
                      <small style={{ fontSize: "10px" }}>
                        {`${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`}
                      </small>
                    </div>
                    <div
                      style={{
                        height: "35px",
                        width: "35px",
                        borderRadius: "4px",
                        backgroundColor: "rgb(230, 230, 230)",
                        border: "1px solid #dee2e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "4px",
                        marginRight: "4px",
                      }}
                    >
                      <span style={{ fontSize: "8px", fontWeight: "bold", color: "#6c757d" }}>0%</span>
                    </div>
                  </div>
                );
              }

              const percentageValue = avgData.averagePercentage || 0;
              const percentageDisplay = percentageValue.toFixed(1);
              const color = getUtilizationColor(percentageValue);
              const textColor = getTextColor(percentageValue);
              const isOverCapacity = avgData.averageCount > floorCapacity;

              return (
                <div
                  key={hour}
                  className="d-flex flex-column align-items-center"
                  style={{ minWidth: "40px", cursor: "pointer" }}
                  onClick={() => {
                    setSelectedAverage(avgData);
                    setShowAverageBreakdown(true);
                  }}
                  title={`${hour}:00 - Avg ${avgData.averageCount} people (${percentageDisplay}%)`}
                >
                  <div className="text-center mb-2">
                    <small style={{ fontSize: "10px" }}>
                      {`${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`}
                    </small>
                  </div>
                  <div
                    style={{
                      height: "35px",
                      width: "35px",
                      borderRadius: "4px",
                      backgroundColor: color,
                      border: isOverCapacity ? "2px solid #dc3545" : "1px solid #dee2e6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "4px",
                      marginRight: "4px",
                    }}
                  >
                    <span style={{ fontSize: "8px", fontWeight: "bold", color: textColor }}>
                      {percentageDisplay}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showZoneBreakdown && selectedEntry && (
        <>
          <ZoneBreakdownModal
            entry={selectedEntry}
            floorCapacity={floorCapacity}
            onClose={() => {
              setShowZoneBreakdown(false);
              setSelectedEntry(null);
            }}
          />
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowZoneBreakdown(false);
              setSelectedEntry(null);
            }}
          ></div>
        </>
      )}

      {showAverageBreakdown && selectedAverage && (
        <>
          <AverageBreakdownModal
            entry={selectedAverage}
            floorCapacity={floorCapacity}
            onClose={() => {
              setShowAverageBreakdown(false);
              setSelectedAverage(null);
            }}
          />
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowAverageBreakdown(false);
              setSelectedAverage(null);
            }}
          ></div>
        </>
      )}
    </div>
  );
};
